import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Calendar, Clock, User, Mail, Phone, MessageSquare, Check } from "lucide-react";
import Appointment from "../entities/Appointment.json";
import BookingNotification from "../entities/BookingNotification.json";
import { useNavigate } from "react-router-dom";

// Complete SERENITY services list
const services = [
  { name: "التسـويق الرقمـي", category: "تسويق" },
  { name: "الهوية البصرية والعلامة التجارية",  category: "تصميم" },
  { name: "الإنتاج الإعلامي والتصوير",  category: "تصوير" },
  { name: "الطباعة والإنتاج المطبعي",  category: "طباعه" },
  { name: "تنظيم الفعاليات والإيفنتات",  category: "تنظيم فعاليات" },
  { name: "الاستشارات التسويقية",  category: "استشارات" },
];

const timeSlots = [
  "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
  "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM",
  "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM", "5:00 PM", "5:30 PM", "6:00 PM"
];

export default function BookingModal({ isOpen, onClose, initialService }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    client_name: "",
    email: "",
    phone: "",
    service: "",
    preferred_date: "",
    preferred_time: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      if (initialService) {
        setFormData(prev => ({ ...prev, service: initialService.name }));
        setStep(2); // Skip service selection
      } else {
        resetForm();
      }
      setError("");
    }
  }, [isOpen, initialService]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError("");
  };

  const validateForm = () => {
    const { client_name, email, phone, service, preferred_date, preferred_time } = formData;
    
    if (!client_name.trim()) return "الرجـاء إدخـال اسـمـك الـكـامـل";
    if (!email.trim()) return "الرجـاء إدخـال الـبريـد الإلـكـتـرونـي";
    if (!email.includes("@")) return "الرجـاء إدخـال بـريـد إلـكـتـرونـي صـحـيـح";
    if (!phone.trim()) return "الرجـاء إدخـال رقـم الهـاتـف";
    if (!service) return "الرجـاء اخـتـيـار الخـدمـة الـمـراد حـجـزهـا";
    if (!preferred_date) return "الرجاء اخـتـيـار التـاريـخ الـمـنـاسـب";
    if (!preferred_time) return "الرجـاء اخـتـيـار الـوقـت الـمـنـاسـب";
    
    const selectedDate = new Date(preferred_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      return "الرجاء اختيار تاريخ مستقبلي";
    }
    
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      // حفظ البيانات على CPanel/MySQL
      await fetch("/api/save_appointment.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      // أولا ننتقل للصفحة، ثم نغلق المودال
      navigate("/success");
      resetForm();
      onClose();
    } catch (error) {
      console.error('Booking submission failed:', error);
      setError('حدث خطأ تقني أثناء الإرسال. يرجى المحاولة مرة أخرى أو الاتصال بنا مباشرة على الرقم ⁦+966 57 937 9779⁩.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      client_name: "",
      email: "",
      phone: "",
      service: "",
      preferred_date: "",
      preferred_time: "",
      message: ""
    });
    setStep(1);
    setError("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="font-cairo fixed inset-0 bg-[#141d3c]/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 50 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="font-cairo bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="font-cairo sticky top-0 bg-white rounded-t-3xl border-b border-gray-100 p-6 flex items-center justify-between">
              <div className="font-cairo flex items-center gap-3">
                <h2 className="font-cairo text-2xl font-bold text-[#141d3c]">اطلـب عرضـك الخـاص !</h2>
              </div>
              <button
                onClick={handleClose}
                className="font-cairo w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center"
              >
                <X className="font-cairo w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="font-cairo p-6">
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"
                >
                  {error}
                </motion.div>
              )}

              {step === 1 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-6"
                >
                  <div className="text-center mb-8">
                    <p className="font-cairo text-gray-600">اخـتـر خـدمـتـك : </p>
                  </div>
                  <div className="font-cairo grid gap-4 max-h-96 overflow-y-auto">
                    {services.map((service) => (
                      <div
                        key={service.name}
                        onClick={() => {
                          handleInputChange('service', service.name);
                          setStep(2);
                        }}
                        className={`font-cairo p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 hover:shadow-md ${
                          formData.service === service.name
                            ? 'border-[#141d3c] bg-[#141d3c]/5'
                            : 'border-gray-200 hover:border-[#141d3c]/50'
                        }`}
                      >
                        <h3 className="font-cairo text-lg font-semibold text-[#141d3c]">{service.name}</h3>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-6"
                >
                  <div className="font-cairo text-center mb-8">
                    <p className="font-cairo text-gray-600">ادخـل تفـاصـيـل طلبك : </p>
                    <div className="font-cairo mt-4 p-4 bg-[#141d3c]/5 rounded-xl">
                      <p className="font-cairo text-lg text-[#0F0F0F]">{formData.service}</p>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="font-cairo grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="font-cairo block text-sm font-medium text-gray-700 mb-2">
                          <User className="w-4 h-4 inline mr-2" /> الاسم الكامل *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.client_name}
                          onChange={(e) => handleInputChange('client_name', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#141d3c]"
                          placeholder="اسمك الكامل"
                        />
                      </div>
                      <div>
                        <label className="font-cairo block text-sm font-medium text-gray-700 mb-2">
                          <Mail className="w-4 h-4 inline mr-2" /> البريد الإلكتروني *
                        </label>
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#141d3c]"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Phone className="w-4 h-4 inline mr-2" /> رقم الهاتف *
                      </label>
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#141d3c]"
                        placeholder="+966 5XX XXX XXX"
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Calendar className="w-4 h-4 inline mr-2" /> التاريخ *
                        </label>
                        <input
                          type="date"
                          required
                          value={formData.preferred_date}
                          onChange={(e) => handleInputChange('preferred_date', e.target.value)}
                          min={getTomorrowDate()}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#141d3c]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Clock className="w-4 h-4 inline mr-2" /> الوقت *
                        </label>
                        <select
                          required
                          value={formData.preferred_time}
                          onChange={(e) => handleInputChange('preferred_time', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#141d3c]"
                        >
                          <option value="">اختر الوقت</option>
                          {timeSlots.map((time) => (
                            <option key={time} value={time}>{time}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <MessageSquare className="w-4 h-4 inline mr-2" /> محتوى الرسالة (اختياري)
                      </label>
                      <textarea
                        value={formData.message}
                        onChange={(e) => handleInputChange('message', e.target.value)}
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#141d3c] resize-none"
                        placeholder="اكتب رسالتك هنا..."
                      />
                    </div>

                    <div className="flex gap-4">
                      <button
                        type="button"
                        onClick={() => setStep(1)}
                        className="flex-1 py-3 px-6 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50"
                      >
                        عودة
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex-1 py-3 px-6 bg-[#141d3c] text-white rounded-xl hover:bg-[#ee9805] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        ) : (
                          'تأكيد'
                        )}
                      </button>
                    </div>
                  </form>
                </motion.div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
